"use client"

import { useState, useRef, useEffect } from "react"
import { Send, Loader2, Sparkles, ArrowUp, Loader } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { PromptInput, PromptInputTextarea, PromptInputActions } from "@/components/ui/prompt-input"
import { cn } from "@/lib/utils"

type ChatMessage = {
  id: string
  role: "user" | "assistant"
  content: string
}

const MOCK_RESPONSES = [
  "Based on this contract, the royalty rate is set at 15% of net receipts.",
  "The term of this agreement is 3 years from the effective date, with an option to extend.",
  "I found a termination clause in Section 7 that allows either party to terminate with 30 days written notice.",
  "The contract includes a non-compete provision that restricts the artist for 2 years post-termination.",
  "Looking at the payment terms, advances are recoupable against future royalties at 100%.",
]

export function ChatPanel() {
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isTyping, setIsTyping] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, isTyping])

  const handleSubmit = () => {
    if (!input.trim() || isTyping) return

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: input.trim(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput("")
    setIsTyping(true)

    setTimeout(() => {
      const aiMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: MOCK_RESPONSES[Math.floor(Math.random() * MOCK_RESPONSES.length)],
      }
      setMessages((prev) => [...prev, aiMessage])
      setIsTyping(false)
    }, 1200 + Math.random() * 800)
  }

  return (
    <>
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="size-16 rounded-lg bg-accent flex items-center justify-center mb-3">
                <img
                  src="/img/logo.png"
                  alt="Logo"
                  className="size-8 object-contain shrink-0 invert dark:invert-0 transition-opacity group-hover:opacity-0 pointer-events-none select-none"
                />
              </div>
              <p className="font-medium">Contract Assistant</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-[200px]">
                Ask questions about terms, clauses, or obligations in this contract.
              </p>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                "flex",
                msg.role === "user" ? "justify-end" : "justify-start"
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-3 py-2 text-sm",
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-md"
                    : "bg-muted rounded-bl-md"
                )}
              >
                {msg.content}
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-2xl rounded-bl-md px-3 py-2">
                <div className="flex items-center gap-1">
                  <span className="size-1.5 bg-foreground/40 rounded-full animate-bounce [animation-delay:0ms]" />
                  <span className="size-1.5 bg-foreground/40 rounded-full animate-bounce [animation-delay:150ms]" />
                  <span className="size-1.5 bg-foreground/40 rounded-full animate-bounce [animation-delay:300ms]" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="p-3 border-t shrink-0">
        <PromptInput
          value={input}
          onValueChange={setInput}
          onSubmit={handleSubmit}
          maxHeight={120}
          className="bg-muted/50 text-foreground"
          disabled={isTyping}
        >
          <PromptInputTextarea className="text-foreground" placeholder="Ask about this contract…" />
          <PromptInputActions className="justify-end px-2 pb-1">
            <Button
              size="icon-lg"
              className="size-8 rounded-full"
              onClick={handleSubmit}
              disabled={!input.trim() || isTyping}
            >
              {isTyping ? (
                <Loader className="size-4 animate-spin" />
              ) : (
                <ArrowUp className="size-4" />
              )}  
            </Button>
          </PromptInputActions>
        </PromptInput>
      </div>
    </>
  )
}

